import { observer } from 'mobx-react-lite';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { IUserImageAreaModel } from 'src/store/_models/userImageArea';
import React from 'react';
import { ImageMissingPlaceholder } from '../image-missing-placeholder/ImageMissingPlaceholder';
import amplitude from '../../_shared/amplitude';
import { AnalyticsEvents } from '../../_shared/analyticsEvents';
import { ImageLoadingPlaceholder } from '@photosi/rn-components/image-loading-placeholder/ImageLoadingPlaceholder';
import { useResponsiveScale } from '../../contexts/ResponsiveScaleContext';
import { getUserImageAreaTestID } from './UserImageArea.utils';
import { useStore } from 'src/contexts/StoreContext';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { Neutrals } from 'src/theme-v2/colors';

export type UserImageAreaProps = {
  userImageArea: IUserImageAreaModel;
  disabled?: boolean;
  showText?: boolean;
  focusOrder?: number;
};

export const UserImageAreaEmpty = observer(
  ({ focusOrder, userImageArea, disabled = false, showText = true }: UserImageAreaProps) => {
    const { responsiveScaleSV, responsiveScale } = useResponsiveScale();
    const { dynamicModal } = useStore();

    const animatedStyle = useAnimatedStyle(
      () => ({
        position: 'absolute',
        left: userImageArea.x * responsiveScaleSV.value,
        top: userImageArea.y * responsiveScaleSV.value,
        width: userImageArea.width * responsiveScaleSV.value,
        height: userImageArea.height * responsiveScaleSV.value,
        backgroundColor: Neutrals.coldGreys.GreyCold100,
      }),
      [responsiveScaleSV, userImageArea.height, userImageArea.width, userImageArea.x, userImageArea.y, responsiveScale],
    );

    const hasUserImage = !!userImageArea.hasUserImage;

    const onPressHandler = async () => {
      dynamicModal.openModal({
        dynamicModalView: DYNAMIC_MODAL_VIEWS.UploadMultiSource,
        isReplacingPhoto: true,
        uploadMultiSourceCallback: userImageArea.addImages,
      });
      amplitude.trackEvent(AnalyticsEvents.personalize.addPhotoClick);
    };

    const testID = getUserImageAreaTestID(userImageArea, hasUserImage ? 'isLoading' : 'noImage');
    return (
      <Animated.View key={`UserImageArea-${userImageArea.id}`} testID={testID} style={animatedStyle}>
        {hasUserImage ? (
          <ImageLoadingPlaceholder productId={userImageArea.productId} userImageAreaId={userImageArea.id} />
        ) : (
          <ImageMissingPlaceholder
            disabled={disabled}
            onPress={onPressHandler}
            showText={showText}
            focusId={testID}
            focusOrder={focusOrder ?? 0}
            focusGroup={FocusGroups.PRODUCT}
          />
        )}
      </Animated.View>
    );
  },
);
